const buttonTypes = ["button", "submit", "reset"] as const;
/**
 * Defines button types
 */
export type ButtonType = (typeof buttonTypes)[number];

export namespace ButtonType {
  export function isButtonType(value: any): value is ButtonType {
    //@ts-ignore
    return typeof value === "string" && buttonTypes.includes(value);
  }
}

const cssSizes = ["px", "pt", "em", "rem", "%"] as const;
/**
 * Defines CSS sizes
 */
export type CssSize = (typeof cssSizes)[number];

export namespace CssSize {
  export function isCssSize(value: any): value is CssSize {
    //@ts-ignore
    return typeof value === "string" && cssSizes.includes(value);
  }
}

const viewWidth = "vw" as const;
/**
 * Defines width size
 */
export type WidthSize = typeof viewWidth;

export namespace WidthSize {
  export function isWidthSize(value: any): value is WidthSize {
    //@ts-ignore
    return typeof value === "string" && value === viewWidth;
  }
}

const viewHeight = "vh" as const;
/**
 * Defines height size
 */
export type HeightSize = typeof viewHeight;

export namespace HeightSize {
  export function isHeightSize(value: any): value is HeightSize {
    //@ts-ignore
    return typeof value === "string" && value === viewHeight;
  }
}

const percentage = "%" as const;
/**
 * Defines height size
 */
export type PercentageSize = typeof viewHeight;

export namespace PercentageSize {
  export function isPercentageSize(value: any): value is PercentageSize {
    //@ts-ignore
    return typeof value === "string" && value === percentage;
  }
}

const bcIconNames = [
  "sort-by-name-asc",
  "sort-by-name-desc",
  "sort-by-value-asc",
  "sort-by-value-desc",
  "apply-filter",
  "sort-by-sort-id",
] as const;
export type BcIconName = (typeof bcIconNames)[number];

const designVersions = ["6", "7", "7new"] as const;
/**
 * Defines the design version
 */
export type DesignVersion = (typeof designVersions)[number];

export namespace DesignVersion {
  export function isDesignVersion(value: any): value is DesignVersion {
    //@ts-ignore
    return typeof value === "string" && designVersions.includes(value);
  }
}

const extendedDesignVersions = ["6", "7", "7new", "dark", "light", "mouseMode"] as const;
export type ExtendedDesignVersion = (typeof extendedDesignVersions)[number];

export namespace ExtendedDesignVersion {
  export function isExtendedDesignVersion(value: any): value is ExtendedDesignVersion {
    //@ts-ignore
    return typeof value === "string" && extendedDesignVersions.includes(value);
  }
}

const tooltipColors = [
  "blue",
  "red",
  "orange",
  "blue-grey",
  "red-grey",
  "orange-grey",
] as const;
export type TooltipColor = (typeof tooltipColors)[number];

export namespace TooltipColor {
  export function isTooltipColor(value: any): value is TooltipColor {
    //@ts-ignore
    return typeof value === "string" && tooltipColors.includes(value);
  }
}

const dropdownTypes = ["dropdown", "select"] as const;
/**
 * Defines dropdown types
 */
export type DropdownType = (typeof dropdownTypes)[number];

export namespace DropdownType {
  export function isDropdownType(value: any): value is DropdownType {
    //@ts-ignore
    return typeof value === "string" && dropdownTypes.includes(value);
  }
}

const transitionEasings = [
  "ease",
  "linear",
  "ease-in",
  "ease-out",
  "ease-in-out",
] as const;
/**
 * Defines the transition easings
 */
export type TransitionEasing = (typeof transitionEasings)[number];

export namespace TransitionEasing {
  export function isTransitionEasing(value: any): value is TransitionEasing {
    //@ts-ignore
    return typeof value === "string" && transitionEasings.includes(value);
  }
}

const controlSizes = ["sm", "md", "lg"] as const;
/**
 * Defines the control size
 */
export type ControlSize = (typeof controlSizes)[number];

export namespace ControlSize {
  export function isControlSize(value: any): value is ControlSize {
    //@ts-ignore
    return typeof value === "string" && controlSizes.includes(value);
  }
}

const placements = [
  "left-start",
  "left",
  "left-end",
  "right-start",
  "right",
  "right-end",
  "top-start",
  "top",
  "top-end",
  "bottom-start",
  "bottom",
  "bottom-end",
] as const;
/**
 * Defines the placement of an element
 */
export type Placement = (typeof placements)[number];

export namespace Placement {
  export function isPlacement(value: any): value is Placement {
    //@ts-ignore
    return typeof value === "string" && placements.includes(value);
  }
}

const positioningStrategies = ["absolute", "fixed"] as const;
/**
 * Defines the positioning strategy of an element
 */
export type PositioningStrategy = (typeof positioningStrategies)[number];

export namespace PositioningStrategy {
  export function isPositioningStrategy(value: any): value is PositioningStrategy {
    //@ts-ignore
    return typeof value === "string" && positioningStrategies.includes(value);
  }
}

/**
 * Defines a point with x and y coordinates
 */
export type Point = {
  x: number;
  y: number;
};

const inputTypes = [
  "text",
  "number",
  "email",
  "password",
  "search",
  "url",
  "tel",
  "date",
  "time",
  "range",
  "color",
] as const;
/**
 * Defines the BC input types
 */
export type InputType = (typeof inputTypes)[number];

export namespace InputType {
  export function isInputType(value: any): value is InputType {
    //@ts-ignore
    return typeof value === "string" && inputTypes.includes(value);
  }
}

export type OptionItems<T = any> = T[] | Func<T[] | PromiseLike<T[]>> | PromiseLike<T[]>;

export namespace OptionItems {
  export async function resolveItems<T = any>(items: OptionItems<T>): Promise<T[]> {
    let resolved: T[] | undefined = undefined;
    switch (typeof items) {
      case "function":
        const itemsResult = items();
        if (PromiseLike.isPromiseLike(itemsResult)) {
          resolved = await itemsResult;
        } else {
          resolved = itemsResult;
        }
        break;
      case "object":
        if (Array.isArray(items)) {
          resolved = items;
        } else if (PromiseLike.isPromiseLike(items)) {
          resolved = await items;
        }
    }
    if (!resolved) {
      throw new Error("The items provided is not valid");
    } else {
      return resolved;
    }
  }
}

export namespace PromiseLike {
  export function isPromiseLike<T = any>(obj: any): obj is PromiseLike<T> {
    if (obj && "then" in obj && typeof obj.then === "function") return true;
    return false;
  }
}

export type Func<T = any> = () => T;

const modalPositions = [
  "top-left",
  "top-center",
  "top-right",
  "middle-left",
  "middle-center",
  "middle-right",
  "bottom-left",
  "bottom-center",
  "bottom-right",
] as const;
/**
 * Defines the modal position
 */
export type ModalPosition = (typeof modalPositions)[number];

export namespace ModalPosition {
  export function isModalPosition(value: any): value is ModalPosition {
    //@ts-ignore
    return typeof value === "string" && modalPositions.includes(value);
  }
}

const checkboxCheckmarks = ["square", "tick", "tick-outline"] as const;
/**
 * Defines the checkbox checkmark style
 */
export type CheckboxCheckmark = (typeof checkboxCheckmarks)[number];

export namespace CheckboxCheckmark {
  export function isCheckboxCheckmark(value: any): value is CheckboxCheckmark {
    //@ts-ignore
    return typeof value === "string" && checkboxCheckmarks.includes(value);
  }
}

const radioCheckmarks = ["circle", "circle-outline"] as const;
/**
 * Defines the radio button checkmark style
 */
export type RadioCheckmark = (typeof radioCheckmarks)[number];

export namespace RadioCheckmark {
  export function isRadioCheckmark(value: any): value is RadioCheckmark {
    //@ts-ignore
    return typeof value === "string" && radioCheckmarks.includes(value);
  }
}

const bgImages = [
  "dm6-weather0-colored",
  "dm6-weather0",
  "dm6-weather1",
  "dm6-weather2",
  "dm6-weather3",
  "dm6-weather-1",
  "dm6-weather-2",
  "dm6-weather-3",
  "dm7-blau",
  "dm7-rot",
  "dm7-orange",
  "fill",
] as const;
/**
 * Defines the background image
 */
export type BgImage = (typeof bgImages)[number];

export namespace BgImage {
  export function isBgImage(value: any): value is BgImage {
    //@ts-ignore
    return typeof value === "string" && bgImages.includes(value);
  }
}

const componentPositions = ["bottom", "top"] as const;
/**
 * Defines the component positions in menu item
 */
export type ComponentPosition = (typeof componentPositions)[number];

export namespace ComponentPosition {
  export function isComponentPosition(value: any): value is ComponentPosition {
    //@ts-ignore
    return typeof value === "string" && componentPositions.includes(value);
  }
}
